<template>
  <SocialWallModalShell :busy="busy"
                        :size="hasPostMedia ? 'wide' : 'default'"
                        :visible="visible"
                        :aria-label="$t('general.social_wall_comments')"
                        :close-label="$t('general.close_comment_window')"
                        v-on="$listeners"
  >
    <template #title>
      <span class="interactive-social-wall__comments-modal-title">
        {{ $t('general.social_wall') }}
      </span>
    </template>

    <div class="interactive-social-wall__modal-body">
      <div class="interactive-social-wall__modal-scroll-area">
        <SocialWallPostPreview :post="post"/>

        <section class="interactive-social-wall__modal-comments">
        <div class="interactive-social-wall__modal-comments-title">
          <strong>{{ $t('general.comments') }}</strong>
          <span>{{ commentCount }}</span>
        </div>

        <div v-if="loading"
             class="interactive-social-wall__modal-comments-state"
             role="status"
        >
          <span aria-hidden="true" class="interactive-social-wall__state-spinner"/>
          <span>{{ $t('general.comments_loading') }}</span>
        </div>

        <div v-else-if="loadError"
             class="interactive-social-wall__modal-comments-state interactive-social-wall__modal-comments-state--error"
             role="alert"
        >
          <span>{{ loadError }}</span>
          <button type="button" @click="$emit('retry')">
            {{ $t('general.try_again') }}
          </button>
        </div>

        <div v-else-if="!hasAnyComments" class="interactive-social-wall__modal-comments-state">
          <span>{{ $t('general.no_comments_be_first') }}</span>
        </div>

        <SocialWallCommentList v-else
                               ref="commentList"
                               :busy="busy"
                               :comments="comments"
                               :current-user="currentUser"
                               :delete-errors="deleteErrors"
                               :deleting-comment-id="deletingCommentId"
                               :reply-error="replyError"
                               :reply-submitting="replySubmitting"
                               v-on="$listeners"
        />
        </section>
      </div>

      <SocialWallCommentForm ref="commentForm"
                             :busy="busy"
                             :current-user="currentUser"
                             :error="commentError"
                             :loading="loading"
                             :submitting="commentSubmitting"
                             v-on="$listeners"
      />
    </div>
  </SocialWallModalShell>
</template>

<script>
import SocialWallCommentForm from "./SocialWallCommentForm.vue";
import SocialWallCommentList from "./SocialWallCommentList.vue";
import SocialWallModalShell from "./SocialWallModalShell.vue";
import SocialWallPostPreview from "./SocialWallPostPreview.vue";
import { countCommentTree } from "../utils/commentTree";

export default {
  name: "SocialWallCommentsModal",
  components: {
    SocialWallCommentForm,
    SocialWallCommentList,
    SocialWallModalShell,
    SocialWallPostPreview,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    post: {
      type: Object,
      default: () => ({}),
    },
    comments: {
      type: Array,
      default: () => [],
    },
    commentCount: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadError: {
      type: String,
      default: "",
    },
    currentUser: {
      type: Object,
      default: () => ({}),
    },
    busy: {
      type: Boolean,
      default: false,
    },
    commentSubmitting: {
      type: Boolean,
      default: false,
    },
    commentError: {
      type: String,
      default: "",
    },
    replySubmitting: {
      type: Boolean,
      default: false,
    },
    replyError: {
      type: String,
      default: "",
    },
    deletingCommentId: {
      type: String,
      default: "",
    },
    deleteErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    hasPostMedia() {
      return (Array.isArray(this.post?.files) ? this.post.files : []).some(
          (file) =>
              Boolean(file?.file_link) && [1, 2].includes(Number(file?.file_type))
      );
    },
    hasAnyComments() {
      return countCommentTree(this.comments) > 0;
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.$nextTick(this.focusNewCommentInput);
      } else {
        this.resetModalForms();
      }
    },
    commentSubmitting(isSubmitting, wasSubmitting) {
      if (!wasSubmitting || isSubmitting || this.commentError) return;
      this.$nextTick(() => this.$refs.commentList?.scrollToFirstComment());
    },
  },
  methods: {
    focusNewCommentInput() {
      this.$refs.commentForm?.focusCommentInput();
    },
    resetModalForms() {
      this.$refs.commentForm?.resetForm();
      this.$refs.commentList?.resetInteractionState();
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__modal-body,
.interactive-social-wall__modal-body * {
  box-sizing: border-box;
}

.interactive-social-wall__modal-body button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
  outline: none;
  box-shadow: none;
}

.interactive-social-wall__comments-modal-title {
  display: block;
  margin-bottom: 0;
  color: #51b83e;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.7px;
  text-transform: uppercase;
}

.interactive-social-wall__modal-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.interactive-social-wall__modal-scroll-area {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: #ccd3dd transparent;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.interactive-social-wall__modal-body > .interactive-social-wall__comment-form {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  background: #ffffff;
  border-top: 1px solid #e5e9ef;
  box-shadow: 0 -8px 20px rgba(15, 23, 42, 0.05);
}

.interactive-social-wall__modal-comments {
  padding: 15px 18px 0;
}

.interactive-social-wall__modal-comments
  > .interactive-social-wall__modal-comment-list {
  max-height: none;
  overflow-y: visible;
}

.interactive-social-wall__modal-comments-title {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
}

.interactive-social-wall__modal-comments-title strong {
  color: #303846;
  font-size: 11px;
  font-weight: 700;
}

.interactive-social-wall__modal-comments-title span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  color: #4cae39;
  font-size: 9px;
  font-weight: 700;
  background: #edf8ea;
  border-radius: 9px;
}

.interactive-social-wall__modal-comments-state {
  display: flex;
  min-height: 52px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  color: #7a8495;
  font-size: 10px;
  line-height: 1.45;
  text-align: center;
  background: #f7f9fa;
  border-radius: 7px;
}

.interactive-social-wall__modal-comments-state--error {
  flex-direction: column;
  color: #b84242;
  background: #fff5f5;
}

.interactive-social-wall__modal-comments-state--error button {
  min-height: 28px;
  padding: 0 10px !important;
  color: #b84242 !important;
  font-size: 9px !important;
  font-weight: 600 !important;
  background: #ffffff !important;
  border: 1px solid #efcaca !important;
  border-radius: 5px;
}

.interactive-social-wall__state-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 1.5px solid rgba(104, 117, 139, 0.32);
  border-top-color: #50b83e;
  border-radius: 50%;
  animation: interactive-social-wall-spin 700ms linear infinite;
}

@keyframes interactive-social-wall-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .interactive-social-wall__modal-comments {
    padding-right: 14px;
    padding-left: 14px;
  }
}
</style>
